import { CommandRegistry } from "@vortex-os/slash-commands";
import { curateCommand, type CurateOptions } from "./commands/curate.js";
import { recallCommand, type RecallOptions } from "./commands/recall.js";
import { decisionCommand } from "./commands/decision.js";
import { reindexCommand } from "./commands/reindex.js";
import { sessionStartCommand } from "./commands/session-start.js";
import { logCommand } from "./commands/log.js";
import { handoffCommand } from "./commands/handoff.js";
import { vortexCommand } from "./commands/vortex.js";
import { agendaCommand } from "./commands/agenda.js";

/**
 * Options accepted by {@link createRitualRegistry}. All fields are optional;
 * each one unlocks an additional command surface when supplied:
 *
 *  - `curate` — when an `LLMJudge` is provided, the `/curate` command from
 *    `@vortex-os/proactive-curator` is registered. Without it the rest of
 *    the rituals work as before and `/curate` is simply absent (graceful
 *    degradation rather than a half-broken command).
 *  - `recall` — when an embedder is provided, the `/recall` command over
 *    `@vortex-os/memory-extended` is registered. Same graceful-degradation
 *    rule: absent the embedder, semantic recall would not work, so the
 *    command is simply not installed.
 */
export interface RitualRegistryOptions {
  readonly curate?: CurateOptions;
  readonly recall?: RecallOptions;
}

/**
 * Build a {@link CommandRegistry} with all ritual commands registered.
 *
 * Hosts that want a subset can register the individual exports instead.
 * This factory exists for the common case — "give me everything" — plus
 * opt-in surfaces that depend on host-supplied adapters (currently
 * `/curate`, which needs an `LLMJudge`).
 */
export function createRitualRegistry(
  options?: RitualRegistryOptions,
): CommandRegistry {
  const registry = new CommandRegistry();
  registry.register(sessionStartCommand);
  registry.register(reindexCommand);
  registry.register(decisionCommand);
  registry.register(logCommand);
  registry.register(handoffCommand);
  registry.register(vortexCommand);
  registry.register(agendaCommand);
  if (options?.curate) {
    registry.register(curateCommand(options.curate));
  }
  if (options?.recall) {
    registry.register(recallCommand(options.recall));
  }
  return registry;
}
